import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { LayoutGrid, ShieldCheck, Zap, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

function PublicShell({ children }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Link to="/"><Logo /></Link>
            <nav className="hidden items-center gap-4 text-sm text-muted-foreground md:flex">
              <Link to="/about" className="hover:text-foreground">About</Link>
              <Link to="/contact" className="hover:text-foreground">Contact</Link>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="outline">
              <Link to={user ? "/app" : "/login"}>{user ? "Open workspace" : "Sign in"}</Link>
            </Button>
          </div>
        </div>
      </header>
      {children}
      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-6">
          <Logo />
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} My Trading Room</p>
        </div>
      </footer>
    </div>
  );
}

export default function AboutPage() {
  return (
    <PublicShell>
      <main className="mx-auto max-w-3xl px-4 py-14 sm:py-20" data-testid="about-page">
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Dashboards shouldn't come pre-decided.
        </h1>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground">
          Every trading analytics site shows you the same fixed page: their layout, their priorities,
          their idea of what matters. But a scalper, an EA vendor and a fund manager have almost nothing
          in common except the login screen.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          My Trading Room flips that. We compute the serious numbers, drawdown, profit factor, expectancy,
          equity curves, from every trade in your history. Then we hand you the widgets and get out of
          the way. Your workspace is yours: what you track, how big it is, where it sits, who sees it.
        </p>
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {[
            { icon: LayoutGrid, title: "Composable", text: "24 widgets, one grid, zero opinions about your layout." },
            { icon: ShieldCheck, title: "Read-only by design", text: "Investor passwords only. We can never touch a trade." },
            { icon: Zap, title: "Fast where it counts", text: "Live account data refreshes automatically every 5 minutes." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border bg-card p-5">
              <f.icon className="h-5 w-5 text-primary" />
              <h3 className="font-display mt-3 text-sm font-semibold">{f.title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border bg-surface-2 p-6 text-center">
          <h2 className="font-display text-lg font-semibold">See it with real data in under a minute.</h2>
          <p className="mt-1 text-sm text-muted-foreground">Three demo accounts with 18 months of history are waiting inside.</p>
          <Button asChild className="mt-4" data-testid="about-cta-button">
            <Link to="/login">Get started free <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </main>
    </PublicShell>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your message. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicShell>
      <main className="mx-auto max-w-3xl px-4 py-14 sm:py-20" data-testid="contact-page">
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Talk to us.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Questions about MT4/MT5 connections or anything else. We read everything.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_280px]">
          <div className="rounded-2xl border bg-card p-6">
            {sent ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="contact-success">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--profit))]/12">
                  <CheckCircle2 className="h-8 w-8 text-[hsl(var(--profit))]" />
                </span>
                <h2 className="font-display text-lg font-semibold">Message sent</h2>
                <p className="max-w-sm text-sm text-muted-foreground">
                  Thanks {form.name.split(" ")[0]}. Your message is in our inbox and we reply fast.
                </p>
                <Button variant="secondary" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}>
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-name">Name</Label>
                    <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Alex Trader" data-testid="contact-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-email">Email</Label>
                    <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-subject">Subject</Label>
                  <Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="What's this about?" data-testid="contact-subject-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-message">Message</Label>
                  <Textarea id="c-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us what you need..." data-testid="contact-message-input" />
                </div>
                <Button type="submit" disabled={busy} data-testid="contact-submit-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send message
                </Button>
              </form>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border bg-card p-5">
              <h3 className="font-display text-sm font-semibold">Support</h3>
              <p className="mt-1 text-sm text-muted-foreground">support@mytradingroom.co</p>
              <p className="mt-0.5 text-xs text-muted-foreground/70">(placeholder, final address coming soon)</p>
            </div>
            <div className="rounded-2xl border bg-card p-5">
              <h3 className="font-display text-sm font-semibold">Office</h3>
              <p className="mt-1 text-sm text-muted-foreground">Address placeholder</p>
              <p className="mt-0.5 text-xs text-muted-foreground/70">City, Country</p>
            </div>
            <div className="rounded-2xl border bg-card p-5">
              <h3 className="font-display text-sm font-semibold">Social</h3>
              <p className="mt-1 text-sm text-muted-foreground">@mytradingroom</p>
              <p className="mt-0.5 text-xs text-muted-foreground/70">(links coming soon)</p>
            </div>
          </div>
        </div>
      </main>
    </PublicShell>
  );
}
